/**
 * @file Renders the checkbox group of investment tiers and keeps the
 * hidden per-tier amount fields synced with the checked tiers.
 */
import { useEffect, useState } from "react";
import { Checkbox, Form, Input, Typography } from "antd";
import type { FormItemProps } from "antd";

import { isBackstage } from "@components/Constants";

import InvestmentTierOption, { KEY_AMOUNT, KEY_AMOUNTS_MAP } from "./InvestmentTierOption";
import type { SubInvestment } from "../../helpers/types";
import type { Form as FormInstance, Tier } from "../types";

import "./InvestmentsInput.css";

const { Text } = Typography;

const KEY_TIER_IDS = "_tierIds";

const LABEL_CLASSES = "What investment class(es) would you like to invest in?";
const LABEL_CLASS_REQUIRED = "Select at least one class";

const GROUP_STYLE = { display: "flex", flexDirection: "column" } as const;

type Props = {
  form: FormInstance;
  name: string[];
  path: string[];
  tiers: Tier[];
  itemProps?: FormItemProps;
};

/**
 * Renders the tier checkboxes for an investment and the per-tier amount
 * inputs they reveal.
 */
const InvestmentsInput = ({
  form,
  path,
  name,
  tiers,
  itemProps = {},
}: Props) => {
  const sourceValue = Form.useWatch(path, form);

  const [ selectedTierIds, setSelectedTierIds ] = useState<string[]>([]);

  useEffect(() => {
    const investments: Partial<SubInvestment>[] = form.getFieldValue(path) || [];

    investments.forEach(({ projectTierId: tierId, amount }) => {
      const amountFieldPath = [ ...path.slice(0, -1), KEY_AMOUNTS_MAP, tierId, KEY_AMOUNT ];
      form.setFieldValue(amountFieldPath, amount);
    });

    const tierIdsFieldPath = [ ...path.slice(0, -1), KEY_TIER_IDS ];
    form.setFieldValue(tierIdsFieldPath, investments.map((item) => item.projectTierId));

    setSelectedTierIds(investments.map((item) => item.projectTierId));
  }, [ form, path, sourceValue ]);

  const rules = [{
    message: LABEL_CLASS_REQUIRED,
    required: true,
  }];

  const options = tiers
    .filter(({ isPrivate }) => (isBackstage ? true : !isPrivate))
    .sort((a, b) => (a.name < b.name ? -1 : 1))
    .map((tier) => ({
      value: tier.name,
      label: (
        <InvestmentTierOption
          name={name}
          form={form}
          path={path}
          tier={tier}
          autoFocus={false}
          selectedTierIds={selectedTierIds}
        />
      ),
    }));

  /**
   * Rebuilds the tier list from the checked tier ids, keeping each
   * tier's existing amount, and updates the checked-tiers state.
   */
  const onChange = (checkedTierIds: string[]) => {
    const investments: Partial<SubInvestment>[] = form.getFieldValue(path) || [];

    const investmentsChanged: Partial<SubInvestment>[] = [];

    for (const tierId of checkedTierIds) {
      const [ investment ] = investments.filter((i) => i.projectTierId === tierId);

      if (investment) {
        investmentsChanged.push(investment);
      } else {
        investmentsChanged.push({ projectTierId: tierId });
      }
    }

    form.setFieldValue(path, investmentsChanged);

    /* note: checking / unchecking group doesn't trigger re-render */
    setSelectedTierIds(checkedTierIds);
  };

  /* note: "tierIds" checkboxes should be outside of scope, so input updates
           do not reset fieldPath values: */
  const tierIdsFieldName = [ ...name.slice(0, -1), KEY_TIER_IDS ];

  return (
    <div className="input-investments">
      <Form.Item
        key={"hidden"}
        name={name}
        hidden={true}
      >
        <Input />
      </Form.Item>

      <Form.Item
        key={"input"}
        name={tierIdsFieldName}
        label={<Text strong>{LABEL_CLASSES}</Text>}
        rules={rules}
        {...itemProps}
      >
        <Checkbox.Group
          style={GROUP_STYLE}
          options={options}
          onChange={onChange}
        />
      </Form.Item>
    </div>
  );
};

export default InvestmentsInput;

export {
  KEY_TIER_IDS,
  KEY_AMOUNTS_MAP,
};
